"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "@/lib/cwfNavigation";
import { useNoEnergyModal } from "@/stores/noEnergyModal";
import {
  ENERGY_COST_PER_MATCH,
  tickEnergyRegen,
  useEnergyMeta,
} from "@/stores/energyMeta";

/** Matches `REGEN_MS_PER_POINT` in `stores/energyMeta.ts`. */
const REGEN_MS_PER_POINT = 3 * 60 * 1000;

function formatCountdown(ms: number): string {
  const safe = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Full-screen "Out of energy" gate — pops up when the player tries to start
 * a match with insufficient energy. Blocks navigation to `/matchmaking` until
 * the user either heads to the store or dismisses.
 */
export function NoEnergyModal() {
  const open = useNoEnergyModal((s) => s.open);
  const hide = useNoEnergyModal((s) => s.hide);
  const router = useRouter();

  const energy = useEnergyMeta((s) => s.energy);
  const energyMax = useEnergyMeta((s) => s.energyMax);
  const updatedAt = useEnergyMeta((s) => s.updatedAt);

  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    if (!open) return;
    tickEnergyRegen();
    setNow(Date.now());
    const id = window.setInterval(() => {
      tickEnergyRegen();
      setNow(Date.now());
    }, 1000);
    return () => window.clearInterval(id);
  }, [open]);

  useEffect(() => {
    if (open && energy >= ENERGY_COST_PER_MATCH) hide();
  }, [open, energy, hide]);

  if (!open || typeof document === "undefined") return null;

  const nextPointAt =
    energy >= energyMax ? null : updatedAt + REGEN_MS_PER_POINT;
  const msToNextPoint = nextPointAt != null ? nextPointAt - now : 0;
  const pointsNeeded = Math.max(0, ENERGY_COST_PER_MATCH - energy);
  const msToPlayable =
    nextPointAt != null
      ? msToNextPoint + Math.max(0, pointsNeeded - 1) * REGEN_MS_PER_POINT
      : 0;

  function visitStore() {
    hide();
    router.push("/store?tab=energy");
  }

  function dismiss() {
    hide();
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[720] flex h-[100dvh] max-h-[100dvh] flex-col"
      role="dialog"
      aria-modal="true"
      aria-labelledby="no-energy-modal-title"
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={dismiss}
        className="absolute inset-0 z-0 cursor-default bg-slate-950/85 backdrop-blur-[3px]"
      />

      <div className="pointer-events-none relative z-[1] flex min-h-0 flex-1 flex-col items-center justify-center px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] sm:px-6">
        <div
          className="pointer-events-auto relative w-full max-w-md rounded-[22px] border-[4px] border-[#3f2312] bg-[#FFF7DD] p-5 shadow-[0_28px_72px_rgba(0,0,0,0.72),inset_0_2px_0_rgba(255,247,221,0.72)] ring-2 ring-amber-950/25 sm:p-7"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close"
            className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border-[3px] border-[#9f2419] bg-gradient-to-b from-[#FF7D65] to-[#D94132] font-display text-base font-black leading-none text-white shadow-[0_3px_0_rgba(70,20,12,0.55),inset_0_2px_0_rgba(255,255,255,0.35)] transition hover:brightness-105 active:translate-y-0.5"
          >
            <span className="drop-shadow-[0_2px_0_rgba(60,20,12,0.55)]">✕</span>
          </button>

          <div className="flex flex-col items-center text-center">
            <div className="mb-3 flex h-20 w-20 items-center justify-center rounded-full border-[4px] border-[#1d4ed8] bg-gradient-to-b from-[#fde68a] via-[#facc15] to-[#f59e0b] shadow-[0_6px_0_rgba(8,47,74,0.45),inset_0_2px_0_rgba(255,255,255,0.55)] sm:h-24 sm:w-24">
              <svg
                viewBox="0 0 24 24"
                className="h-12 w-12 text-[#1e3a8a] drop-shadow-[0_2px_0_rgba(255,247,221,0.55)] sm:h-14 sm:w-14"
                fill="currentColor"
                aria-hidden
              >
                <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" />
              </svg>
            </div>

            <h2
              id="no-energy-modal-title"
              className="font-display text-[clamp(1.35rem,5.2vw,1.7rem)] font-black uppercase tracking-[0.06em] text-[#7B422F] drop-shadow-[0_1px_0_rgba(255,247,221,0.65)]"
            >
              Out of Energy
            </h2>
            <p className="mt-3 text-[0.95rem] font-semibold leading-relaxed text-[#5a3017] sm:text-[1rem]">
              You need <span className="font-black">{ENERGY_COST_PER_MATCH}</span>{" "}
              energy to start a match. Wait for it to refill, or grab a boost
              from the store.
            </p>

            <div className="mt-4 w-full rounded-2xl border-2 border-[#d4b48a] bg-[#FFF1C9] px-4 py-3 shadow-inner">
              <div className="flex items-center justify-between gap-3">
                <div className="text-left">
                  <p className="font-display text-[10px] font-bold uppercase tracking-[0.16em] text-[#7B422F]/80">
                    Energy
                  </p>
                  <p className="mt-0.5 font-display text-lg font-black tabular-nums text-[#7B422F]">
                    {energy}
                    <span className="text-[#7B422F]/60">/{energyMax}</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-display text-[10px] font-bold uppercase tracking-[0.16em] text-[#7B422F]/80">
                    {pointsNeeded > 0 ? "Playable in" : "Next +1"}
                  </p>
                  <p className="mt-0.5 font-display text-lg font-black tabular-nums text-[#7B422F]">
                    {nextPointAt != null
                      ? formatCountdown(
                          pointsNeeded > 0 ? msToPlayable : msToNextPoint,
                        )
                      : "—"}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-5 flex w-full flex-col gap-2 sm:flex-row sm:gap-3">
              <button
                type="button"
                onClick={visitStore}
                className="order-1 w-full rounded-xl border-[3px] border-[#0a355c] bg-gradient-to-b from-[#5eb7ff] to-[#1e74d4] py-3 font-display text-sm font-black uppercase tracking-wide text-white shadow-[inset_0_2px_0_rgba(255,255,255,0.35),0_4px_0_rgba(8,47,74,0.45)] transition hover:brightness-105 active:translate-y-px sm:order-2 sm:flex-1"
              >
                Visit Shop
              </button>
              <button
                type="button"
                onClick={dismiss}
                className="order-2 w-full rounded-xl border-[3px] border-[#7B422F]/55 bg-[#fff7e4] py-3 font-display text-sm font-black uppercase tracking-wide text-[#7B422F] shadow-[inset_0_2px_0_rgba(255,255,255,0.55),0_3px_0_rgba(123,66,47,0.35)] transition hover:brightness-[1.03] active:translate-y-px sm:order-1 sm:flex-1"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
